import { decimalToLocalizedString } from "../../../../../../lib/Money";

export type InvoiceEmailMessageContext = {
    invoiceNumber: string | null;
    totalAmount: number | null;
    paymentTermDays: number | null;
    customerName?: string | null;
    entrepreneurName?: string | null;
    companyName?: string | null;
};

const DEFAULT_PAYMENT_TERM_DAYS = 14;

function greetingFor(customerName?: string | null): string {
    const name = customerName?.trim();
    return name ? `Beste ${name},` : "Beste klant,";
}

function amountFor(totalAmount: number | null): string {
    return totalAmount === null ? "" : ` van € ${decimalToLocalizedString(totalAmount)}`;
}

// A credit note's total is stored negative; the customer reads what is credited to them.
function creditedAmountOf(totalAmount: number | null): number | null {
    return totalAmount === null ? null : Math.abs(totalAmount);
}

function signatureFor(context: InvoiceEmailMessageContext): string[] {
    return [context.entrepreneurName?.trim(), context.companyName?.trim()].filter(Boolean) as string[];
}

export function buildInvoiceEmailIntro(context: InvoiceEmailMessageContext): string {
    const invoiceLabel = context.invoiceNumber ? ` ${context.invoiceNumber}` : "";

    return [
        greetingFor(context.customerName),
        "",
        `Hierbij ontvang je factuur${invoiceLabel}${amountFor(context.totalAmount)}.`,
    ].join("\n");
}

export function buildInvoiceReminderIntro(context: InvoiceEmailMessageContext): string {
    const invoiceLabel = context.invoiceNumber ? ` ${context.invoiceNumber}` : "";

    return [
        greetingFor(context.customerName),
        "",
        `Waarschijnlijk is het je ontgaan: factuur${invoiceLabel}${amountFor(context.totalAmount)} ` +
            `staat nog open. Wil je deze alsnog voldoen?`,
    ].join("\n");
}

export function buildCreditNoteEmailIntro(context: InvoiceEmailMessageContext): string {
    const creditNoteLabel = context.invoiceNumber ? ` ${context.invoiceNumber}` : "";

    return [
        greetingFor(context.customerName),
        "",
        `Hierbij ontvang je creditfactuur${creditNoteLabel}` +
            `${amountFor(creditedAmountOf(context.totalAmount))}.`,
    ].join("\n");
}

export function buildDefaultInvoiceEmailBody(context: InvoiceEmailMessageContext): string {
    const term = context.paymentTermDays ?? DEFAULT_PAYMENT_TERM_DAYS;

    return [
        `Graag betalen binnen ${term} dagen.`,
        "",
        "Hartelijke groet,",
        ...signatureFor(context),
    ].join("\n");
}

export function buildDefaultCreditNoteEmailBody(context: InvoiceEmailMessageContext): string {
    return [
        "Je hoeft niets te betalen; het bedrag wordt met je verrekend.",
        "",
        "Hartelijke groet,",
        ...signatureFor(context),
    ].join("\n");
}

export function buildDefaultInvoiceReminderBody(context: InvoiceEmailMessageContext): string {
    return [
        "Heb je de betaling net gedaan? Dan kun je dit bericht negeren.",
        "",
        "Hartelijke groet,",
        ...signatureFor(context),
    ].join("\n");
}

export function composeInvoiceEmailMessage(intro: string, body: string): string {
    const trimmedBody = body.trim();
    return trimmedBody ? `${intro}\n\n${trimmedBody}` : intro;
}
